import * as React from 'react';
import classnames from 'classnames';
import { CoreRadioButton as CoreRadio } from '../internal/WixUiCoreComponents/components/CoreRadioButton';
import { st, classes } from './Ratings.st.css';
import { Favorite, FavoriteFill } from '@wix/wix-ui-icons-common/on-stage';
import { FavoriteFillLarge, FavoriteLarge, } from '@wix/wix-ui-icons-common/on-stage/system';
import { RATINGS_DATA_HOOKS, RATINGS_DATA_KEYS } from './dataHooks';
import { ErrorMessageWrapper } from '../internal/ErrorMessageWrapper';
import { RatingsSize, RatingsMode, RatingsLayout, RatingsLabelPlacement, RatingsDataHooks, } from './types';
import uniqueId from '../../common/uniqueId';
const STARS_INDEXES_ARR = [0, 1, 2, 3, 4];
const partialIconBuilder = (IconFill, iconSize) => (coveredPart) => (React.createElement("div", { className: classes.partialIconContainer, style: { width: iconSize, height: iconSize } },
    React.createElement(IconFill, { className: classes.partialIconBackground }),
    React.createElement("div", { className: classes.partialIconOverlay, style: {
            width: `${coveredPart * 100}%`,
        } },
        React.createElement(IconFill, null))));
const icons = {
    small: {
        empty: React.createElement(Favorite, null),
        full: React.createElement(FavoriteFill, null),
        partial: partialIconBuilder(FavoriteFill, 24),
    },
    large: {
        empty: React.createElement(FavoriteLarge, null),
        full: React.createElement(FavoriteFillLarge, null),
        partial: partialIconBuilder(FavoriteFillLarge, 32),
    },
};
const toNearestStep = (valueToRound, step = 0.5) => parseFloat((Math.round(valueToRound / step) * step).toFixed(1));
/** Ratings component based on IconToggle */
export class Ratings extends React.Component {
    constructor(props) {
        super(props);
        this.starsRefs = STARS_INDEXES_ARR.map(() => React.createRef());
        this.state = {
            currentHovered: -1,
        };
        this.focus = () => {
            const { value } = this.props;
            const indexToFocus = value === 0 ? 0 : value - 1;
            const refToFocus = this.starsRefs[indexToFocus];
            refToFocus?.current?.focus();
        };
        this._labelId = uniqueId('rating-label-id');
        this._ratingsName = uniqueId('ratings');
        this._handleHoverIcon = (idx) => {
            this.setState({ currentHovered: idx });
        };
        this._onClick = (idx) => {
            const { mode, onSelect } = this.props;
            if (mode === RatingsMode.Input && onSelect) {
                onSelect(idx);
            }
        };
        this._renderInputOptions = () => {
            const { inputOptions, value } = this.props;
            const { currentHovered } = this.state;
            return (React.createElement("div", { className: classes.labelList }, currentHovered > -1 ? (React.createElement("span", { "data-hook": RATINGS_DATA_HOOKS.InputOption, className: classes.inputOption }, inputOptions[currentHovered])) : (React.createElement("span", { "data-hook": RATINGS_DATA_HOOKS.InputOptionCurrent, className: classnames(classes.inputOption, classes.inputOptionCurrent) }, inputOptions[value - 1]))));
        };
        this._renderRatingInfo = () => {
            const { ratingDisplay, countDisplay } = this.props;
            const info = [];
            if (ratingDisplay) {
                info.push(ratingDisplay);
            }
            if (countDisplay) {
                info.push(countDisplay);
            }
            return (React.createElement("div", { "data-hook": RATINGS_DATA_HOOKS.RatingInfo, className: classes.ratingInfo }, info.join(' | ')));
        };
        this._renderIcon = (idx) => {
            const { mode, value, size } = this.props;
            const { currentHovered } = this.state;
            const isDisplay = mode === 'display';
            const isHovered = currentHovered >= 0 && idx <= currentHovered;
            const isSelected = idx < value;
            let iconTheme = 'empty';
            if (isDisplay || isHovered || (isSelected && currentHovered < 0)) {
                iconTheme = 'full';
            }
            let icon = icons[size][iconTheme];
            if (isDisplay && Math.floor(value) === idx) {
                const coveredPart = toNearestStep(value % 1);
                icon = coveredPart ? icons[size].partial(coveredPart) : icon;
            }
            return icon;
        };
        this._handleListHover = ({ target }) => {
            if (target.tagName === 'DIV' && target.classList.contains(classes.icon)) {
                const hoveredIndex = parseInt(target.dataset.index, 10);
                const currentHovered = hoveredIndex >= 0 ? hoveredIndex : -1;
                this.setState({ currentHovered });
            }
        };
        this._handleListUnHover = () => {
            this.setState({ currentHovered: -1 });
        };
        this._onFocus = (idx) => {
            this._handleHoverIcon(idx);
        };
        this._onBlur = () => {
            this._handleHoverIcon(-1);
        };
        this._errorMessageId = uniqueId('error-message');
    }
    _getDataAttributes() {
        const { currentHovered } = this.state;
        const { disabled, error, size } = this.props;
        return {
            [RATINGS_DATA_KEYS.Disabled]: disabled,
            [RATINGS_DATA_KEYS.Error]: error,
            [RATINGS_DATA_KEYS.Size]: size,
            [RATINGS_DATA_KEYS.Hovered]: currentHovered + 1,
        };
    }
    render() {
        const { value, disabled, error, errorMessage, size, mode, inputOptions, ratingDisplay, countDisplay, className, label, labelPlacement, layout, starsAriaLabels, errorAppearance, lang, } = this.props;
        const ratingList = Array.from(new Array(5));
        const showInputOptions = inputOptions.length && mode === RatingsMode.Input;
        const showRatingInfo = (ratingDisplay || countDisplay) && mode === RatingsMode.Display;
        const isErrorMessageProvided = error && !!errorMessage && mode === RatingsMode.Input;
        const isInputOptionSelected = showInputOptions && value > 0 && value <= 5;
        const stStates = {
            disabled,
            error,
            hasErrorMessage: !!errorMessage,
            size,
            mode,
            layout,
            showInputOptions: !!showInputOptions,
            inputOptionSelected: isInputOptionSelected,
            labelPlacement,
        };
        return (React.createElement(ErrorMessageWrapper, { lang,
            visible: isErrorMessageProvided,
            message: errorMessage,
            messageId: this._errorMessageId,
            errorAppearance,
            className: st(classes.root, stStates, className),
            'data-hook': this.props['data-hook'] },
            React.createElement("div", { className: classes.container, ...this._getDataAttributes(), role: "group", "aria-labelledby": mode === RatingsMode.Input && label ? this._labelId : undefined, "data-hook": RatingsDataHooks.RatingContainer },
                mode === RatingsMode.Input && label && (React.createElement("div", { id: this._labelId, "data-hook": RATINGS_DATA_HOOKS.RatingLabel, className: classes.label }, label)),
                React.createElement("div", { className: classes.innerWrapper },
                    React.createElement("div", { className: `${classes.iconList} ${!value ? classes.empty : ''}`, tabIndex: mode === RatingsMode.Display ? -1 : undefined, onMouseEnter: this._handleListHover, onMouseLeave: this._handleListUnHover, "aria-hidden": mode === RatingsMode.Display }, ratingList.map((_el, idx) => {
                        const checked = idx + 1 === Math.floor(value);
                        const defaultAriaLabel = inputOptions.length
                            ? inputOptions[idx]
                            : idx.toString();
                        return (React.createElement(CoreRadio, { ref: this.starsRefs[idx], value: `${idx + 1}`, "aria-label": (starsAriaLabels && starsAriaLabels[idx]) ||
                                defaultAriaLabel, uncheckedIcon: this._renderIcon(idx), checkedIcon: this._renderIcon(idx), checked: checked, disabled: mode === RatingsMode.Display || disabled, onChange: () => this._onClick(idx + 1), onFocus: () => this._onFocus(idx), onBlur: this._onBlur, name: this.props.name || this._ratingsName, onHover: () => this._handleHoverIcon(idx), "data-hook": RATINGS_DATA_HOOKS.IconWrapper, className: st(classes.icon, { checked }), key: idx, "data-index": idx }));
                    })),
                    !!showInputOptions && this._renderInputOptions(),
                    !!showRatingInfo && this._renderRatingInfo()))));
    }
}
Ratings.displayName = 'Ratings';
Ratings.defaultProps = {
    value: 0,
    disabled: false,
    error: false,
    mode: RatingsMode.Input,
    size: RatingsSize.Small,
    layout: RatingsLayout.Aside,
    inputOptions: [],
    starsAriaLabels: [],
    labelPlacement: RatingsLabelPlacement.Top,
};
//# sourceMappingURL=Ratings.js.map